import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowClockwiseIcon, CaretDownIcon, CursorClickIcon, XIcon } from '@phosphor-icons/react';

import { useDevServerStatus } from '@/api-models/dev-server';
import { normalizePreviewPath, portInfo, previewProxyUrl, useSandboxPorts } from '@/api-models/ports';
import { Button } from '@/ui/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { Spinner } from '@/ui/Spinner';

import { useSelectionStore, type ElementSelectionPayload } from '../../stores/selection-store';

import { DevServerStrip } from './preview/DevServerStrip';

/** Ports a dev server is most likely to be on, best first. */
const DEV_SERVER_PORTS = [3000, 5173, 3333, 8000, 8080];

/** Messages the injected agent sends up (see `preview_select.js`). */
type AgentMessage =
  | { source: 'zooid-preview'; type: 'ready'; route: string }
  | { source: 'zooid-preview'; type: 'route'; route: string }
  | { source: 'zooid-preview'; type: 'select-mode'; enabled: boolean }
  | { source: 'zooid-preview'; type: 'selection'; selection: ElementSelectionPayload };

function isAgentMessage(data: unknown): data is AgentMessage {
  return typeof data === 'object' && data !== null && (data as { source?: unknown }).source === 'zooid-preview';
}

function defaultPort(ports: number[]): number | null {
  const preferred = DEV_SERVER_PORTS.find((p) => ports.includes(p));
  // Anything left is a guess, so at least skip the desktop/VNC ports the sandbox
  // runs itself — framing those is what the Desktop tab is for.
  return preferred ?? ports.find((p) => !portInfo(p)?.system) ?? null;
}

/**
 * In-app browser for a port in the session's sandbox, with an element picker
 * whose selections are handed to the composer.
 *
 * The iframe points at our own preview proxy rather than at the signed Daytona
 * URL. That is what makes the picker possible at all: the proxy appends a
 * selection agent to the document, so the page can be interrogated over
 * `postMessage` instead of being an opaque cross-origin frame. See
 * `routes/agent_sessions/preview.rs`. Ports remain openable on their own from the
 * Ports tab, which hands out the signed URL.
 */
export function PreviewTab({ base }: { base: string }) {
  const { ports: scannedPorts, isLoading } = useSandboxPorts(base);
  // Same SWR key as the strip below, so this shares that poll rather than adding one.
  const { status: devServer } = useDevServerStatus(base, true);
  const [chosenPort, setChosenPort] = useState<number | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [selecting, setSelecting] = useState(false);
  /**
   * The path the iframe is *loaded* at. Part of its key, so changing this remounts the frame.
   *
   * Deliberately not updated when the app routes on its own: a client-side navigation must not
   * remount the frame, or the SPA would be torn down and rebuilt on every link click — losing
   * exactly the state someone opened the preview to look at.
   */
  const [loadedPath, setLoadedPath] = useState('/');
  /** Where the page says it actually is now, which after client-side routing is not `loadedPath`. */
  const [route, setRoute] = useState('/');
  /** What is in the box. Held apart from `route` so the page's own navigations do not overwrite
   *  a path the user is part-way through typing. */
  const [draft, setDraft] = useState('/');
  const editing = useRef(false);
  const frame = useRef<HTMLIFrameElement>(null);

  /**
   * The dev server's port straight from the supervisor that started it.
   *
   * Not a duplicate of the port scan: the scan is Daytona's, on a 5s poll, and it is the only
   * thing this tab used to trust — so a server that was genuinely up could still be missing from
   * the menu, and the tab would say there was nothing to preview. The sidecar knows the port
   * exactly, the moment it is listening. It is merged in rather than replacing the scan, because
   * a session may well be serving something this tool did not start.
   */
  const devPort = devServer?.state === 'ready' ? devServer.port : null;
  const ports =
    devPort === null || scannedPorts.includes(devPort)
      ? scannedPorts
      : [...scannedPorts, devPort].sort((a, b) => a - b);

  // The dev server outranks any heuristic guess — it is the thing this tab exists to show.
  const port = chosenPort ?? devPort ?? defaultPort(ports);
  const selections = useSelectionStore((s) => s.selections);
  const addSelection = useSelectionStore((s) => s.addElement);
  const clearSelections = useSelectionStore((s) => s.clear);

  /** Follow the page's own navigation in the box only — never by remounting it. */
  const syncRoute = useCallback((next: string) => {
    const normalized = normalizePreviewPath(next);
    setRoute(normalized);
    if (!editing.current) setDraft(normalized);
  }, []);

  /** Load a path, by remounting the frame at it. */
  const go = useCallback((next: string) => {
    const normalized = normalizePreviewPath(next);
    setDraft(normalized);
    setRoute(normalized);
    setLoadedPath(normalized);
    // Bumped even when the path is unchanged: re-submitting the same path is how someone asks
    // for a fresh load, and the key alone would not change.
    setReloadNonce((n) => n + 1);
  }, []);

  const setSelectMode = useCallback((enabled: boolean) => {
    setSelecting(enabled);
    frame.current?.contentWindow?.postMessage(
      { source: 'zooid-parent', type: 'select-mode', enabled },
      window.location.origin,
    );
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The frame is same-origin precisely because it goes through our proxy, so
      // this is an exact check rather than a preview-host pattern.
      if (event.origin !== window.location.origin || !isAgentMessage(event.data)) return;

      switch (event.data.type) {
        case 'ready':
          // A reload or an in-page navigation replaces the agent, which starts
          // out idle — re-arm it so the toggle keeps meaning what it says.
          if (selecting) setSelectMode(true);
          syncRoute(event.data.route);
          break;
        case 'route':
          // Client-side routing: the app moved without a document load, so this is the only
          // signal that the box is now showing the wrong path.
          syncRoute(event.data.route);
          break;
        case 'select-mode':
          // Escape inside the page turns the mode off; mirror that in the button.
          setSelecting(event.data.enabled);
          break;
        case 'selection':
          addSelection(event.data.selection);
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [addSelection, selecting, setSelectMode, syncRoute]);

  if (isLoading && ports.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Spinner size={5} />
      </div>
    );
  }

  if (port === null) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <DevServerStrip base={base} />
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 px-6 text-center">
          <p className="text-sm text-fg-secondary">Nothing to preview yet.</p>
          {/* Used to say "start a dev server in the sandbox", which sent the user elsewhere to do
              the one thing this tab exists for. The Start button above does it. */}
          <p className="text-xs text-fg-tertiary">Start the dev server above, and its pages will show up here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DevServerStrip base={base} />
      <div className="flex h-10 shrink-0 items-center gap-1.5 border-b border-line-subtle px-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size={7} iconRight={<CaretDownIcon size={12} />}>
              localhost:{port}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {ports.map((p) => (
              <DropdownMenuItem key={p} onSelect={() => setChosenPort(p)}>
                <span className="font-mono">{p}</span>
                <span className="text-fg-tertiary">{portInfo(p)?.label ?? ''}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <form
          className="min-w-0 flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            editing.current = false;
            go(draft);
          }}
        >
          <input
            value={draft}
            onChange={(e) => {
              editing.current = true;
              setDraft(e.target.value);
            }}
            onFocus={(e) => {
              editing.current = true;
              e.target.select();
            }}
            onBlur={() => {
              // Abandoning an edit restores what is actually on screen, rather than leaving the
              // box asserting a path the page never went to.
              editing.current = false;
              setDraft(route);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                editing.current = false;
                setDraft(route);
                e.currentTarget.blur();
              }
            }}
            spellCheck={false}
            autoComplete="off"
            aria-label="Preview path"
            placeholder="/"
            className="w-full min-w-0 rounded-md border border-line-subtle bg-surface-2 px-2 py-1 font-mono text-2xs text-fg-secondary placeholder:text-fg-tertiary focus:border-brand-border focus:text-fg focus:outline-none"
          />
        </form>

        <Button
          variant={selecting ? 'primary' : 'ghost'}
          size={7}
          iconLeft={<CursorClickIcon size={13} />}
          onClick={() => setSelectMode(!selecting)}
        >
          Select element
        </Button>

        {selections.length > 0 && (
          <span className="flex items-center gap-1 rounded-full border border-brand-border bg-brand-tint py-0.5 pr-1 pl-2.5 text-2xs font-medium text-brand-text">
            {selections.length} {selections.length === 1 ? 'selection' : 'selections'}
            <button
              type="button"
              onClick={clearSelections}
              aria-label="Clear selections"
              className="rounded-full p-0.5 hover:bg-brand/15"
            >
              <XIcon size={10} />
            </button>
          </span>
        )}

        <div className="ml-auto">
          <Button
            variant="ghost"
            shape="square"
            size={7}
            // Reloads where the page *is*, as a browser's reload does — not where it was first
            // loaded. After client-side routing those differ, and snapping back to the original
            // path would read as the button navigating rather than reloading.
            onClick={() => go(route)}
            aria-label="Reload preview"
          >
            <ArrowClockwiseIcon size={13} />
          </Button>
        </div>
      </div>

      {/* Keyed on the port, the loaded path and the reload counter: remounting is the reload,
          and it also drops the old document's agent rather than leaving two armed. `loadedPath`
          rather than `route` on purpose — see its declaration: keying on where the app has
          client-side routed to would remount the SPA on every link click. */}
      <iframe
        key={`${port}:${loadedPath}:${reloadNonce}`}
        ref={frame}
        src={previewProxyUrl(base, port, loadedPath)}
        title="Sandbox preview"
        className="min-h-0 flex-1 border-0 bg-white"
      />
    </div>
  );
}
